import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import api from './api';
import { AppThunk } from './app';
// import { stringEncryption } from '../config/Global';
import API_URL from '../config/ApiUrl';

interface AuthState {
  user: any;
  appLoading: boolean;
  initialData: object | null;
  isLoading: boolean;
  userDetail: any;
  token: string | null;
  userListData: any | null;
  useName: string | null;
}

const initialState = {
  appLoading: true,
  initialData: {},
  isLoading: false,
  userDetail: null,
  token: null,
  userListData: [],
  useName: '',
} as AuthState;

const AuthSlice = createSlice({
  name: 'AUTH',
  initialState,
  reducers: {
    setAppLoading: (state, action: PayloadAction<boolean>) => {
      state.appLoading = action.payload;
    },
    setInitialData: (state, action: PayloadAction<any>) => {
      state.initialData = action.payload;
    },
    setUserToken: (state, action: PayloadAction<string | null>) => {
      state.token = action.payload;
    },
    setUserDetail: (state, action: PayloadAction<any>) => {
      state.userDetail = action.payload;
    },
    resetAuthStore: () => {
      return initialState; // Reset the state to the initial state
    },
    start: (state) => {
      state.isLoading = true;
    },
    success: (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      localStorage.setItem('token', action.payload?.data?.token);
    },
    failure: (state) => {
      state.isLoading = false;
    },
    setUserListData: (state, action: PayloadAction<any>) => {
      state.userListData = action?.payload?.data ? action?.payload?.data : [];
    },
    setUserName: (state, action: PayloadAction<any>) => {
      state.useName = action?.payload ? action?.payload : '';
    },
  },
});

export const {
  start,
  success,
  failure,
  resetAuthStore,
  setAppLoading,
  setInitialData,
  setUserDetail,
  setUserToken,
  setUserName,
} = AuthSlice.actions;

// Async action to fetch todos from the API
export const doLogin =
  (action: any): AppThunk<any> =>
  async (dispatch) => {
    try {
      dispatch(start());
      const response = await api.post(API_URL.LOGIN, action);
      // if (action?.remember) {
      //   const string = JSON.stringify({
      //     email: action.username,
      //     password: action.password,
      //   });
      //   localStorage.setItem('remember_me', stringEncryption(string));
      // }

      dispatch(success(response.data));
      dispatch(setUserToken(response.data.token));
      dispatch(setUserOptions());

      return Promise.resolve(response.data);
    } catch (error: any) {
      dispatch(failure());
      return Promise.reject(error.data);
    }
  };

export const doRegister =
  (action: any): AppThunk<any> =>
  async (dispatch) => {
    try {
      dispatch(start());
      const response = await api.post(API_URL.REGISTER, action);
      // dispatch(success(response.data));
      dispatch(setUserToken(response.data));
      // dispatch(setUserOptions());

      return Promise.resolve(response.data);
    } catch (error: any) {
      dispatch(failure());
      return Promise.reject(error.data);
    }
  };

// Async action to fetch sync from the API
export const initialAppOptions = (): AppThunk<any> => async (dispatch) => {
  try {
    const token = localStorage.getItem('token') ?? null;
    let initialData = localStorage.getItem('initialData') ?? null;

    if (token) {
      dispatch(setUserToken(token));
      // dispatch(setUserOptions());
    }

    // if (!initialData) {
    //   const response = await api.post(API_URL.COMMON.SYNC);
    //   dispatch(setInitialData(response.data.data));
    //   localStorage.setItem('initialData', JSON.stringify(response.data.data));
    //   dispatch(setAppLoading(false));

    //   initialData = response.data;
    // } else {
    //   initialData = JSON.parse(initialData);
    // }

    dispatch(setAppLoading(false));
    return Promise.resolve(initialData);
  } catch (error: any) {
    dispatch(setAppLoading(false));
    return Promise.reject(error.data);
  }
};

export const setUserOptions = (): AppThunk<any> => async (dispatch) => {
  try {
    const response = await api.post(API_URL.USER.ME);
    //console.log('response.data', response.data.data);
    const userDetails = {
      ...response.data.data,
    };
    localStorage.setItem('userDetails', JSON.stringify(userDetails));
    dispatch(setUserName(response.data?.data?.full_name));
    dispatch(setAppLoading(false));
    dispatch(setUserDetail(userDetails));
    return Promise.resolve(response.data);
  } catch (error: any) {
    dispatch(setAppLoading(false));
    dispatch(setUserToken(null));
    localStorage.removeItem('token');
    return Promise.reject(error.data);
  }
};

export const doLogout =
  (action: any): AppThunk<any> =>
  async (dispatch) => {
    try {
      const response = await api.post(API_URL.LOGOUT, action);
      if (response) {
        dispatch(setUserToken(null));
        dispatch(setUserDetail(null));
        dispatch(setInitialData(null));
        localStorage.clear();
        initialAppOptions();
      } else {
        dispatch(setUserToken(null));
        dispatch(setUserDetail(null));
        dispatch(setInitialData(null));
        localStorage.clear();
        initialAppOptions();
      }
      return Promise.resolve(response);
    } catch (error: any) {
      return Promise.reject(error.data);
    }
  };

export const forgotPassword =
  (action: any): AppThunk<any> =>
  async (dispatch) => {
    try {
      dispatch(start());
      const response = await api.post(API_URL.FORGOT_PASSWORD, action);
      // dispatch(success(response.data));

      return Promise.resolve(response.data);
    } catch (error: any) {
      dispatch(failure());
      return Promise.reject(error.data);
    }
  };

export const updatePassword =
  (action: any): AppThunk<any> =>
  async (dispatch) => {
    try {
      dispatch(start());
      const response = await api.post(API_URL.RESET_PASSWORD, action);
      // dispatch(success(response.data));

      return Promise.resolve(response.data);
    } catch (error: any) {
      dispatch(failure());
      return Promise.reject(error.data);
    }
  };

export const checkOTP =
  (action: any): AppThunk<any> =>
  async (dispatch) => {
    try {
      dispatch(start());
      const response = await api.post(API_URL.CHECK_OTP, action);
      return Promise.resolve(response.data);
    } catch (error: any) {
      dispatch(failure());
      return Promise.reject(error.data);
    }
  };

export default AuthSlice.reducer;
